/*
 * scroll-reveal.js — native CSS + IntersectionObserver reveal engine.
 *
 * Rebuilt 31 Jul 2026 perf pass. Previously this file fetched GSAP core +
 * ScrollTrigger from cdnjs.cloudflare.com on every page (a third-party DNS
 * lookup + TLS handshake + two SERIAL requests, ~1.7s wall-clock under a
 * throttled mobile connection per the CPU-profile + resource-timing trace
 * taken before this change; GSAP's own parse/execute cost was a much
 * smaller ~100ms of that even 4x-CPU-throttled). The fade+rise/stagger
 * effect it drives needs no JS engine at all — CSS transitions plus
 * IntersectionObserver reproduce the same enter/leave/enter-back/leave-back
 * behaviour GSAP's ScrollTrigger gave us, for free, on the main thread,
 * with no network dependency.
 *
 * Public API is unchanged: window.ScrollReveal.refresh(root), fired on
 * DOMContentLoaded and on shopify:section:load, so theme.liquid's script
 * tag and every data-reveal-* attribute already in the sections needed
 * zero changes.
 *
 * Supported attributes (same contract as the old GSAP version):
 *   data-reveal="up|down|left|right"   direction (default up)
 *   data-reveal-distance="48"          px offset (default 48)
 *   data-reveal-duration="0.72"        seconds (default 0.72)
 *   data-reveal-delay="0.1"            seconds (default 0)
 *   data-reveal-once                   don't re-hide on scroll away
 *   data-reveal-start="top 88%"        ScrollTrigger-style start line
 *   data-reveal-group + data-reveal-stagger="0.1"   staggered container
 *   data-reveal-child                  member of a data-reveal-group
 *
 * Traded on purpose: the old GSAP build eased the "leaving" animation
 * differently from the "entering" one (quicker, fixed up-and-left drift).
 * This version uses the same transition both ways. Since that difference
 * only played while an element was already scrolling out of view, it is
 * not a visible regression in practice.
 *
 * prefers-reduced-motion: reduce — elements render fully visible, no
 * transition, no IntersectionObserver work at all (see the injected
 * stylesheet below).
 */
!(function () {
  "use strict";

  var DEFAULT_DISTANCE = 48;
  var DEFAULT_DURATION = 0.72;
  var DEFAULT_STAGGER = 0.1;
  var DEFAULT_START = "top 88%";
  var EASE = "cubic-bezier(.165,.84,.44,1)";

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function injectStyles() {
    if (document.getElementById("pga-scroll-reveal-styles")) return;
    var style = document.createElement("style");
    style.id = "pga-scroll-reveal-styles";
    style.textContent =
      "[data-reveal]{opacity:0;transform:translate3d(var(--reveal-x,0),var(--reveal-y,0),0);" +
      "transition:opacity var(--reveal-duration," + DEFAULT_DURATION + "s) " + EASE + "," +
      "transform var(--reveal-duration," + DEFAULT_DURATION + "s) " + EASE + ";" +
      "transition-delay:var(--reveal-delay,0s);will-change:opacity,transform}" +
      "[data-reveal].reveal-in{opacity:1;transform:translate3d(0,0,0)}" +
      "@media (prefers-reduced-motion: reduce){" +
      "[data-reveal]{opacity:1!important;transform:none!important;transition:none!important}}";
    document.head.appendChild(style);
  }

  function offsetFor(el) {
    var dir = (el.dataset.reveal || el.dataset.revealDir || "up").toLowerCase();
    var dist = parseFloat(el.dataset.revealDistance) || DEFAULT_DISTANCE;
    if (dir === "down") return { x: 0, y: -dist };
    if (dir === "left") return { x: dist, y: 0 };
    if (dir === "right") return { x: -dist, y: 0 };
    return { x: 0, y: dist }; // "up" and default
  }

  function applyVars(el, delay) {
    var off = offsetFor(el);
    el.style.setProperty("--reveal-x", off.x + "px");
    el.style.setProperty("--reveal-y", off.y + "px");
    el.style.setProperty("--reveal-duration", (parseFloat(el.dataset.revealDuration) || DEFAULT_DURATION) + "s");
    el.style.setProperty("--reveal-delay", (delay || 0) + "s");
  }

  // Converts a ScrollTrigger-style "top NN%" start line into an
  // IntersectionObserver rootMargin that fires at the same point.
  function rootMarginFor(el) {
    var start = (el.dataset.revealStart || DEFAULT_START).trim();
    var m = /^top\s+(\d+(?:\.\d+)?)%$/.exec(start);
    var pct = m ? parseFloat(m[1]) : 88;
    return "0px 0px " + -(100 - pct) + "% 0px";
  }

  function observe(el, once, onToggle) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          onToggle(true);
          if (once) io.disconnect();
        } else if (!once) {
          onToggle(false);
        }
      });
    }, { rootMargin: rootMarginFor(el), threshold: 0 });
    io.observe(el);
    return io;
  }

  function initStandalone(el) {
    if (el._srInit) return;
    el._srInit = true;
    if (reducedMotion) return; // stylesheet already forces it visible
    applyVars(el, parseFloat(el.dataset.revealDelay) || 0);
    observe(el, el.hasAttribute("data-reveal-once"), function (visible) {
      el.classList.toggle("reveal-in", visible);
    });
  }

  function initGroup(group) {
    if (group._srInit) return;
    group._srInit = true;
    var children = Array.prototype.slice.call(group.querySelectorAll("[data-reveal-child]"));
    if (!children.length) return;
    if (reducedMotion) return; // stylesheet already forces them visible
    var stagger = parseFloat(group.dataset.revealStagger) || DEFAULT_STAGGER;
    children.forEach(function (child, i) {
      applyVars(child, (parseFloat(child.dataset.revealDelay) || 0) + i * stagger);
    });
    observe(group, group.hasAttribute("data-reveal-once"), function (visible) {
      children.forEach(function (child) {
        child.classList.toggle("reveal-in", visible);
      });
    });
  }

  function init(root) {
    injectStyles();
    root = root || document;
    root.querySelectorAll("[data-reveal-group]").forEach(initGroup);
    root.querySelectorAll("[data-reveal]").forEach(function (el) {
      if (!el.closest("[data-reveal-group]")) initStandalone(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });

  window.ScrollReveal = { refresh: function (root) { init(root || document); } };
})();
